import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import LoginPage from "@/pages/LoginPage";
import StockViewPage from "@/pages/StockViewPage";
import AdminPage from "@/pages/AdminPage";
import QuickUpdatePage from "@/pages/QuickUpdatePage";
import ShipmentsPage from "@/pages/ShipmentsPage";
import MasterAIPage from "@/pages/MasterAIPage";
import ReservationsPage from "@/pages/ReservationsPage";
import { BottomNavBar } from "@/components/BottomNavBar";
import { attachStorageHealthInterceptors, initializeClientStorageMaintenance } from "@/utils/storageMaintenance";

// Enable credentials globally so session cookie is sent with every request
axios.defaults.withCredentials = true;
attachStorageHealthInterceptors(axios);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const IDLE_WARNING_MS = 25 * 60 * 1000;
const IDLE_LOGOUT_MS = 30 * 60 * 1000;

// OAuthCallback — handles Google OAuth redirect with session_id hash
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function OAuthCallback({ handleLogin }) {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const match = hash.match(/session_id=([^&\s]+)/);
    if (!match) {
      navigate('/', { replace: true });
      return;
    }

    const sessionId = decodeURIComponent(match[1]);
    axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true })
      .then(res => {
        const role = res.data.role || 'staff';
        handleLogin(role);
        window.history.replaceState(null, '', window.location.pathname);
        navigate('/stock', { replace: true });
      })
      .catch(err => {
        const msg = err.response?.data?.detail || 'Authentication failed. Please try again.';
        toast.error(msg);
        window.history.replaceState(null, '', window.location.pathname);
        navigate('/', { replace: true });
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#DC2626] border-t-transparent"></div>
        <p className="mt-4 text-[#64748b] font-medium">Completing sign-in...</p>
        <p className="text-sm text-[#9CA3AF] mt-2">Please wait...</p>
      </div>
    </div>
  );
}

// Main App Router
function AppRouter() {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [userRole, setUserRole] = useState("");
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("rm_dark_mode") === "true");
  const [notifCount, setNotifCount] = useState(0);
  const navigate = useNavigate();
  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const lastNotifIds = useRef(new Set());
  const notifInitialized = useRef(false);

  // Apply dark mode class to html element
  useEffect(() => {
    if (darkMode) document.documentElement.classList.add("dark");
    else document.documentElement.classList.remove("dark");
  }, [darkMode]);

  const toggleDarkMode = useCallback(() => {
    setDarkMode(prev => {
      const next = !prev;
      localStorage.setItem("rm_dark_mode", String(next));
      if (next) document.documentElement.classList.add("dark");
      else document.documentElement.classList.remove("dark");
      return next;
    });
  }, []);

  const handleSmartBack = useCallback(() => {
    navigate('/stock');
  }, [navigate]);

  // Check existing auth on mount
  useEffect(() => {
    // CRITICAL: Skip /auth/me check if returning from OAuth callback — OAuthCallback handles it
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes('session_id=')) {
      return;
    }
    const checkAuth = async () => {
      const storedAuth = localStorage.getItem("auth");
      const storedRole = localStorage.getItem("role");

      if (storedAuth === "true" && storedRole) {
        // Verify session is still valid
        try {
          const response = await fetch(`${API}/auth/me`, {
            credentials: "include",
          });

          if (response.ok) {
            const userData = await response.json();
            setIsAuthenticated(true);
            setUserRole(userData.role || storedRole);
          } else if (response.status === 401) {
            // Session expired — clear stale localStorage and force re-login
            localStorage.removeItem("auth");
            localStorage.removeItem("role");
            setIsAuthenticated(false);
          } else {
            // Other error (network glitch) — trust localStorage to avoid unnecessary logouts
            setIsAuthenticated(true);
            setUserRole(storedRole);
          }
        } catch (error) {
          // Network error — trust localStorage
          setIsAuthenticated(true);
          setUserRole(storedRole);
        }
      } else {
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, []);

  useEffect(() => {
    initializeClientStorageMaintenance();
  }, []);

  const handleLogin = useCallback((role) => {
    setIsAuthenticated(true);
    setUserRole(role);
    localStorage.setItem("auth", "true");
    localStorage.setItem("role", role);
  }, []);

  const handleLogout = useCallback(async () => {
    try {
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      console.error("Logout error:", error);
    }

    setIsAuthenticated(false);
    setUserRole("");
    setNotifCount(0);
    lastNotifIds.current.clear();
    notifInitialized.current = false;
    localStorage.removeItem("auth");
    localStorage.removeItem("role");
  }, []);

  // Notification polling — staff users only, every 30s
  useEffect(() => {
    if (!isAuthenticated || userRole !== "staff") return;

    const checkNotifs = async () => {
      try {
        const res = await fetch(`${API}/notifications`, { credentials: "include" });
        if (!res.ok) return;
        const data = await res.json();
        const notifs = data.notifications || [];
        const newNotifs = notifs.filter(n => !lastNotifIds.current.has(n.notification_id));
        if (notifInitialized.current && newNotifs.length > 0) {
          newNotifs.forEach(n => {
            if (n.type === "approved") toast.success(n.title, { description: n.message, duration: 6000 });
            else if (n.type === "rejected") toast.error(n.title, { description: n.message, duration: 6000 });
            else toast.warning(n.title, { description: n.message, duration: 6000 });
          });
        }
        notifs.forEach(n => lastNotifIds.current.add(n.notification_id));
        notifInitialized.current = true;
        setNotifCount(data.unread_count || 0);
      } catch (_) { /* non-fatal */ }
    };

    checkNotifs();
    const timer = setInterval(checkNotifs, 30000);
    return () => clearInterval(timer);
  }, [isAuthenticated, userRole]);

  const clearNotifCount = useCallback(async () => {
    if (notifCount === 0) return;
    try {
      await fetch(`${API}/notifications/read`, { method: "POST", credentials: "include" });
      setNotifCount(0);
      lastNotifIds.current.clear();
      notifInitialized.current = false;
    } catch (_) { /* non-fatal */ }
  }, [notifCount]);

  useEffect(() => {
    if (!isAuthenticated) {
      setShowIdleWarning(false);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
      return undefined;
    }

    const resetIdleTimers = () => {
      setShowIdleWarning(false);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);

      warningTimerRef.current = setTimeout(() => {
        setShowIdleWarning(true);
      }, IDLE_WARNING_MS);

      logoutTimerRef.current = setTimeout(() => {
        setShowIdleWarning(false);
        toast.warning("Session timed out due to inactivity.");
        handleLogout();
      }, IDLE_LOGOUT_MS);
    };

    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    events.forEach((eventName) => window.addEventListener(eventName, resetIdleTimers, { passive: true }));
    resetIdleTimers();

    return () => {
      events.forEach((eventName) => window.removeEventListener(eventName, resetIdleTimers));
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    };
  }, [handleLogout, isAuthenticated]);

  // Synchronous OAuth callback detection — must come after all hooks
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
    return <OAuthCallback handleLogin={handleLogin} />;
  }

  // Show loading while checking auth
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#DC2626] border-t-transparent"></div>
          <p className="mt-4 text-[#64748b] font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <>
    {showIdleWarning && isAuthenticated && (
      <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4" data-testid="idle-timeout-warning-modal">
        <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl border border-black/5 p-6">
          <p className="text-[11px] uppercase tracking-[0.24em] text-[#DC2626] font-semibold">Security timeout</p>
          <h2 className="heading-font text-2xl text-[#09090b] mt-2">Still working?</h2>
          <p className="text-sm text-[#64748b] mt-2">Your session will be signed out soon due to inactivity. Stay signed in to continue safely.</p>
          <div className="mt-5 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={handleLogout}
              className="px-4 py-2 rounded-xl border border-slate-200 text-slate-700 text-sm font-medium"
              data-testid="idle-logout-now-button"
            >
              Logout now
            </button>
            <button
              type="button"
              onClick={() => setShowIdleWarning(false)}
              className="px-4 py-2 rounded-xl bg-[#09090b] text-white text-sm font-medium"
              data-testid="idle-stay-signed-in-button"
            >
              Stay signed in
            </button>
          </div>
        </div>
      </div>
    )}
    <Routes>
      <Route
        path="/"
        element={
          isAuthenticated ? (
            <Navigate to={userRole === "logistics" ? "/reservations" : "/stock"} replace />
          ) : (
            <LoginPage onLogin={handleLogin} />
          )
        }
      />
      <Route
        path="/stock"
        element={
          isAuthenticated ? (
            <StockViewPage userRole={userRole} onLogout={handleLogout} />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route
        path="/admin"
        element={
          isAuthenticated && userRole === "admin" ? (
            <AdminPage userRole={userRole} onLogout={handleLogout} darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
          ) : (
            <Navigate to="/stock" replace />
          )
        }
      />
      <Route
        path="/quick-update"
        element={
          isAuthenticated && (userRole === "admin" || userRole === "logistics") ? (
            <QuickUpdatePage userRole={userRole} onLogout={handleLogout} />
          ) : (
            <Navigate to="/stock" replace />
          )
        }
      />
      <Route
        path="/shipments"
        element={
          isAuthenticated ? (
            <ShipmentsPage userRole={userRole} onLogout={handleLogout} />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route
        path="/master-ai"
        element={
          isAuthenticated && userRole === "admin" ? (
            <MasterAIPage onBack={handleSmartBack} darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
          ) : (
            <Navigate to="/stock" replace />
          )
        }
      />
      <Route
        path="/reservations"
        element={
          isAuthenticated ? (
            <ReservationsPage userRole={userRole} onLogout={handleLogout} onClearNotifs={clearNotifCount} />
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    {/* Mobile bottom nav — shown on authenticated pages that aren't login/admin/quick-update */}
    {isAuthenticated && !["admin", "quick-update"].some(p => window.location.pathname.includes(p)) && (
      <BottomNavBar userRole={userRole} onLogout={handleLogout} notifCount={notifCount} />
    )}
    </>
  );
}

export default function App() {
  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </div>
  );
}
